import { ComponentProps, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Autocomplete, TextFieldProps } from '@sbv-clients/ui-shared';

import { useFormFieldError } from '../../hooks/use-field-error';

type Option = { id: string; label: string; value: string };

type FormTextFieldProps<T extends Option> = Omit<TextFieldProps, 'type'> & {
  name: string;
} & { options?: T[] };

type RenderType = ComponentProps<typeof Controller>['render'];

export const FormAutocompleteField = <T extends Option>({
  id,
  name,
  label,
  fullWidth,
  disabled,
  required,
  options,
}: FormTextFieldProps<T>) => {
  const { error, helperText } = useFormFieldError(name);
  const { setValue } = useFormContext();

  const onChange = useCallback(
    (value: string) => {
      setValue(name, value);
    },
    [name, setValue]
  );

  const render: RenderType = useCallback(
    ({ field }) => (
      <Autocomplete
        id={id}
        required={required}
        value={field.value}
        onBlur={field.onBlur}
        label={label}
        helperText={helperText}
        error={error}
        fullWidth={fullWidth}
        disabled={disabled}
        options={options}
        onChange={onChange}
      />
    ),
    [
      disabled,
      error,
      fullWidth,
      helperText,
      id,
      label,
      onChange,
      options,
      required,
    ]
  );

  return <Controller name={name} render={render} />;
};

export default FormAutocompleteField;
